// /src/pages/private/settings/Account.jsx
// input-security: verified — fields: language select (existing); profile theme via AccountProfileThemeSection

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import i18n from 'i18next';
import { UserOutlined, GlobalOutlined } from '@ant-design/icons';
import { availableLanguages } from '../../../locales';
import { useAuth } from '../../../authContext';
import AccountProfileThemeSection from './account/AccountProfileThemeSection';

import loadMoment from '../../../../utils/loadMoment';

function Account() {
  const { t } = useTranslation();
  const {
    email,
    hasMfa,
    registerDate,
    language,
    setLanguage,
    getUserInfo,
  } = useAuth();
  const [formattedRegisterDate, setFormattedRegisterDate] = useState('');
  const languageLabel =
    availableLanguages.find((lang) => lang.code === language)?.name || language;

  useEffect(() => {
    async function loadMomentLibrary() {
      const moment = await loadMoment();
      setFormattedRegisterDate(moment(registerDate).format('DD/MM/YYYY'));
    }

    loadMomentLibrary();
    updateLanguage(language);
  }, [language, registerDate]);

  const handleLanguageChange = (selectedOption) => {
    const newLanguage = selectedOption.value;
    setLanguage(newLanguage);
    getUserInfo();
    updateLanguage(newLanguage);
  };

  const updateLanguage = async (lang) => {
    try {
      const response = await axios.post(
        '/api/language',
        { language: { value: lang } },
        { withCredentials: true },
      );
      if (response.status === 200) {
        i18n.changeLanguage(lang);
      }
    } catch (error) {
      console.error("Erreur lors de l'appel axios:", error);
    }
  };

  const selectProps = {
    isSearchable: true,
    classNamePrefix: 'acct-rs',
  };

  return (
    <div
      className="user-settings-container account-page"
      data-resource="private.settings.account"
      data-action="read"
    >
      <h2 className="settings-h2">{t('AccountSettingsTitle')}</h2>

      <section
        className="account-card account-card--summary"
        aria-labelledby="account-summary-title"
      >
        <div className="account-card__lead">
          <div className="account-card__icon-wrap">
            <UserOutlined className="account-card__icon" aria-hidden />
          </div>
          <div className="account-card__copy">
            <h4 id="account-summary-title" className="account-card__title">
              {t('accountCardSummaryTitle')}
            </h4>
            <p className="account-card__subtitle">{t('accountCardSummaryDesc')}</p>
          </div>
        </div>

        <div className="account-card__metrics">
          <dl>
            <div className="account-metric">
              <dt className="account-metric__label">{t('Email')}</dt>
              <dd className="account-metric__value">{email}</dd>
            </div>
            <div className="account-metric">
              <dt className="account-metric__label">{t('accountMfaLabel')}</dt>
              <dd className="account-metric__value">
                {hasMfa ? t('accountMfaEnabled') : t('accountMfaDisabled')}
              </dd>
            </div>
            <div className="account-metric">
              <dt className="account-metric__label">{t('accountRegisteredLabel')}</dt>
              <dd className="account-metric__value">{formattedRegisterDate}</dd>
            </div>
          </dl>
        </div>
      </section>

      <header className="user-settings-section account-page__section-heading">
        <h3 className="settings-h3">{t('AccountSettingsSection')}</h3>
      </header>

      <div className="account-stack">
        <section
          className="account-card account-card--control"
          aria-labelledby="account-lang-title"
        >
          <div className="account-card__control-row">
            <div className="account-card__lead">
              <div className="account-card__icon-wrap">
                <GlobalOutlined className="account-card__icon" aria-hidden />
              </div>
              <div className="account-card__copy">
                <h4 id="account-lang-title" className="account-card__title">
                  {t('accountCardLanguageTitle')}
                </h4>
                <p className="account-card__subtitle">{t('accountCardLanguageDesc')}</p>
              </div>
            </div>
            <div className="account-card__control">
              <label className="visually-hidden" htmlFor="account-language-select-input">
                {t('SelectLanguage')}
              </label>
              <Select
                {...selectProps}
                inputId="account-language-select-input"
                value={{ label: languageLabel, value: language }}
                onChange={handleLanguageChange}
                options={availableLanguages.map((lang) => ({
                  label: lang.name,
                  value: lang.code,
                }))}
                placeholder={t('SelectLanguage')}
              />
            </div>
          </div>
        </section>

        <header className="user-settings-section account-page__section-heading">
          <h3 className="settings-h3">{t('AccountPersonalizationSection')}</h3>
        </header>

        <AccountProfileThemeSection />
      </div>
    </div>
  );
}

export default Account;
